import React, { useEffect, useMemo, useState } from "react";

/**
 * 📅 PERÍODO EM MÊS/ANO — o formato que o parser de ATS entende.
 *
 * Era uma caixa de texto livre ("2021 - atual", "jan/2020 a dez/2022", "de 2019 até hoje"). O parser
 * espera MM/AAAA consistente: data por extenso, ano sozinho ou formato misturado entre um emprego e
 * outro fazem o campo de datas voltar vazio — e sem datas a triagem não calcula tempo de casa, que é
 * metade do que ela usa para inferir senioridade. A Jobscan mede 37% das rejeições por ATS vindas de
 * leitura errada de data.
 *
 * ⚠️ O QUE JÁ ESTÁ GRAVADO NÃO PODE SUMIR. 237 pessoas preencheram este campo em texto livre. Quando o
 * valor não casa com MM/AAAA, o componente NÃO limpa nem adivinha: mostra a caixa de texto original com
 * um aviso discreto, e a pessoa converte se quiser. Apagar o período de alguém para "arrumar o formato"
 * seria estragar o currículo dela em nome da nossa régua.
 *
 * Escrito em 25/09/2026, junto da correção do PDF (ver CurriculoPdfGenerator).
 */

const MESES = [
  ["01", "jan"], ["02", "fev"], ["03", "mar"], ["04", "abr"], ["05", "mai"], ["06", "jun"],
  ["07", "jul"], ["08", "ago"], ["09", "set"], ["10", "out"], ["11", "nov"], ["12", "dez"],
];

// Ano atual + 1 (quem está estudando põe a previsão de conclusão) até 50 anos atrás.
const ANOS = (() => {
  const fim = new Date().getFullYear() + 1;
  return Array.from({ length: 52 }, (_, i) => String(fim - i));
})();

// 🗓️ 20/10/2026 (Amanda, testando o currículo lido do PDF): "Ago/2020 – Ago/2023" caía no texto livre com o aviso
// laranja. Mês por extenso ou abreviado ("ago", "agosto", "Ago.", "agosto de 2020") é a MESMA data, sem adivinhação:
// vira MM/AAAA antes da leitura.
const NOMES_MES = { jan: "01", janeiro: "01", fev: "02", fevereiro: "02", mar: "03", marco: "03", "março": "03", abr: "04", abril: "04",
  mai: "05", maio: "05", jun: "06", junho: "06", jul: "07", julho: "07", ago: "08", agosto: "08", set: "09", setembro: "09",
  out: "10", outubro: "10", nov: "11", novembro: "11", dez: "12", dezembro: "12" };
const RX_MES_NOME = /\b(janeiro|fevereiro|mar[cç]o|abril|maio|junho|julho|agosto|setembro|outubro|novembro|dezembro|jan|fev|mar|abr|mai|jun|jul|ago|set|out|nov|dez)\.?\s*(?:\/|-|de|\s)\s*(\d{4})\b/gi;
function mesesPorNumero(t) {
  return t.replace(RX_MES_NOME, (_, m, a) => `${NOMES_MES[m.toLowerCase()]}/${a}`)
          .replace(/\b(\d)\/(\d{4})\b/g, "0$1/$2");   // "8/2020" -> "08/2020"
}

/** "03/2021 - atual" -> {mi:"03", ai:"2021", mf:"", af:"", atual:true}. null se não reconhecer. */
export function lerPeriodo(bruto) {
  const t = mesesPorNumero((bruto || "").trim());
  if (!t) return { mi: "", ai: "", mf: "", af: "", atual: false };

  const parte = /(\d{2})\/(\d{4})/g;
  const achados = [...t.matchAll(parte)];
  const ehAtual = /\b(atual|hoje|presente|atualmente)\b/i.test(t);

  // Só reconhece o que está inteiramente em MM/AAAA: um "2021" solto no meio significa que a pessoa
  // escreveu de outro jeito, e aí o texto dela manda.
  const semDatas = t.replace(parte, "").replace(/\b(atual|hoje|presente|atualmente|a|até|de|desde)\b/gi, "")
                    .replace(/[\s\-–—/]/g, "");
  if (semDatas.length > 0 || achados.length === 0) return null;

  const [i, f] = achados;
  if (achados.length === 1) {
    return ehAtual
      ? { mi: i[1], ai: i[2], mf: "", af: "", atual: true }
      : { mi: i[1], ai: i[2], mf: "", af: "", atual: false };   // uma data só = início (é o que escreverPeriodo grava quando falta o fim)
  }
  return { mi: i[1], ai: i[2], mf: f[1], af: f[2], atual: ehAtual };
}

/** {mi,ai,mf,af,atual} -> "03/2021 - 12/2024" | "03/2021 - atual" | "" */
export function escreverPeriodo(v) {
  const ini = v.mi && v.ai ? `${v.mi}/${v.ai}` : "";
  if (!ini) return "";
  if (v.atual) return `${ini} - atual`;
  const fim = v.mf && v.af ? `${v.mf}/${v.af}` : "";
  return fim ? `${ini} - ${fim}` : ini;
}

const VAZIO = { mi: "", ai: "", mf: "", af: "", atual: false };

export default function CampoPeriodo({ valor, onChange, comAtual = true, rotulo = "Período" }) {
  // 🐛 20/10/2026 (Amanda): "eu seleciono ele some". O estado vivia SÓ no texto gravado, e o texto só nasce com mês E
  // ano: escolher o mês primeiro gerava "" e o select voltava para "mês". E só o início ("03/2021", sem fim) não era
  // reconhecido e o campo caía na caixa de texto livre. Agora a escolha parcial fica num rascunho local; o texto
  // gravado continua o mesmo formato, e o rascunho só é trocado quando o valor muda por fora (outro registro, IA).
  const doValor = useMemo(() => lerPeriodo(valor), [valor]);
  const [rasc, setRasc] = useState(doValor || VAZIO);
  useEffect(() => {
    if (doValor && escreverPeriodo(doValor) !== escreverPeriodo(rasc)) setRasc(doValor);
    // Valor reconhecido mas escrito de outro jeito ("Ago/2020 – Ago/2023"): grava já no formato MM/AAAA. É a mesma
    // data — nada some —, e é o formato que o PDF do currículo e os sistemas de triagem leem.
    if (doValor && (valor || "").trim() && escreverPeriodo(doValor) !== (valor || "").trim()) onChange(escreverPeriodo(doValor));
  }, [valor]); // eslint-disable-line react-hooks/exhaustive-deps
  // Texto livre antigo: só quando o valor gravado não é o que o próprio rascunho produziu.
  const livre = doValor === null && (valor || "").trim() !== escreverPeriodo(rasc);
  const lido = rasc;

  // Texto que o componente não reconheceu: mantém como estava, com a saída para converter.
  if (livre) {
    return (
      <div>
        <input
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          aria-label={rotulo}
          placeholder="Período"
          value={valor || ""}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          onClick={() => { setRasc(VAZIO); onChange(""); }}
          className="mt-1 text-[11px] text-amber-700 underline hover:text-amber-900"
          title="Sistemas de triagem leem melhor o período no formato mês/ano"
        >
          usar mês/ano (lido melhor pelos sistemas de triagem)
        </button>
      </div>
    );
  }

  const mudar = (novo) => { setRasc(novo); onChange(escreverPeriodo(novo)); };
  const set = (campo, v) => mudar({ ...lido, [campo]: v });
  const sel = "rounded-lg border border-gray-300 px-2 py-2 text-sm bg-white";

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5">
        <select className={sel} aria-label={`${rotulo}: mês de início`} value={lido.mi} onChange={(e) => set("mi", e.target.value)}>
          <option value="">mês</option>
          {MESES.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
        </select>
        <select className={sel} aria-label={`${rotulo}: ano de início`} value={lido.ai} onChange={(e) => set("ai", e.target.value)}>
          <option value="">ano</option>
          {ANOS.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>

        <span className="text-xs text-gray-400">até</span>

        {lido.atual ? (
          <span className="text-sm font-semibold text-gray-600 px-1">hoje</span>
        ) : (
          <>
            <select className={sel} aria-label={`${rotulo}: mês de saída`} value={lido.mf} onChange={(e) => set("mf", e.target.value)}>
              <option value="">mês</option>
              {MESES.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
            </select>
            <select className={sel} aria-label={`${rotulo}: ano de saída`} value={lido.af} onChange={(e) => set("af", e.target.value)}>
              <option value="">ano</option>
              {ANOS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </>
        )}
      </div>

      {comAtual && (
        <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={lido.atual}
            onChange={(e) => mudar({ ...lido, atual: e.target.checked, mf: "", af: "" })}
          />
          estou aqui até hoje
        </label>
      )}
    </div>
  );
}
